import {
  ALL_COMMAND_DESCRIPTORS,
  getDescriptorById,
} from "@dxf-ai-editor/command-registry";

import { t as translate } from "../i18n";
import type { MessageKey } from "../i18n";
import type { DraftCommandId } from "../scene/draftingHelpers";
import type { PanelId, EditorCommand } from "@dxf-ai-editor/contracts";

export type RibbonTabId =
  // "file" hosts the application output commands (Print / Plot). Listed
  // first to mirror AutoCAD's File / Application menu being the
  // left-most ribbon entry point.
  | "file"
  | "draw"
  | "annotate"
  | "modify"
  | "layout"
  | "propertyflows"
  // Phase J.3 — Block Editor mode replaces the normal ribbon tabs while
  // a BEDIT session is active. Single tab "block-editor" carrying the
  // block-authoring sections + a subset of draw/modify commands.
  | "block-editor";

export type RibbonAction = {
  id: string;
  label: string;
  meta: string;
  glyph: string;
  onClick: () => void;
  /**
   * Optional visual emphasis. "danger" renders the button with the
   * red close-button styling used for "Закрыть редактор блока".
   */
  variant?: "danger" | "primary";
};

export type RibbonSection = {
  title: string;
  actions: RibbonAction[];
};

// Ribbon tab IDs are stable; their human-readable labels are translated
// at render time via t(). Keeping the IDs separate from labels lets us
// switch languages without rebuilding the React tree.
//
// `ribbonTabIds` lists the tabs visible in the **default** mode (i.e.
// outside of BEDIT). The "block-editor" tab is exposed via a separate
// `blockEditorRibbonTabIds` so callers can swap them when isInBlockEdit
// becomes true. App.tsx selects between the two arrays via the
// `state.drawing.activeSpaceId === BLOCK_EDIT_SPACE_ID` selector.
export const ribbonTabIds: RibbonTabId[] = [
  "file",
  "draw",
  "annotate",
  "modify",
  "layout",
  "propertyflows",
];

export const blockEditorRibbonTabIds: RibbonTabId[] = ["block-editor"];

export type RibbonCallbacks = {
  t: (key: string) => string;
  beginDrafting: (
    command: DraftCommandId,
    options?: Record<string, string>,
  ) => void;
  openPanel: (panel: PanelId) => void;
  activateSpace: (
    spaceId: string,
    layoutContext?: "model" | "paper" | "viewport",
  ) => void;
  dispatch: (command: EditorCommand) => void;
  onImportDxf: () => void;
  resetShellState: () => void;
  /**
   * Opens the Print / Plot page-setup dialog. Wired in App.tsx to
   * `printDialog.open()` (the `usePrintDialog` hook) — the same entry
   * point reached by typing PRINT / PLOT / ПЕЧАТЬ at the command line.
   * Surfaced via the "Файл" / "File" ribbon tab's Print button.
   */
  onOpenPrint: () => void;
  /**
   * Opens the PDF-6.9a Page Setup Manager (PAGESETUP / ПАРАМЛИСТ).
   * Wired in App.tsx to `pageSetupDialog.open()` (the
   * `usePageSetupDialog` hook). Surfaced via the "Файл" / "File"
   * ribbon tab next to Print.
   */
  onOpenPageSetup: () => void;
  /**
   * Phase K placeholder — invoked when the user clicks one of the
   * Block Editor ribbon buttons (Параметры / Действия / Видимость /
   * наборы параметров). Phase J wires the ribbon scaffolding only;
   * the actual palette / dialog will be plumbed in Phase K. Until
   * then, the App-level handler surfaces a toast indicating the
   * feature is queued.
   */
  onOpenBlockAuthoringPalette?: (
    pane: "parameters" | "actions" | "visibility" | "parameter-sets",
  ) => void;
};

/**
 * Selection-context info passed into buildRibbonSections. When exactly
 * one BlockReference is selected, the "Контекст блока" / "Block
 * Reference" section is appended to the active tab so the user can
 * jump into BEDIT without typing the command name. A multi-block
 * selection is intentionally NOT considered a context — AutoCAD's
 * contextual tab shows up only for a single-block selection.
 */
export type RibbonSelectionContext = {
  /** True when state.selection is exactly 1 entity AND it is a Block. */
  isSingleBlockReference: boolean;
  /** Block name for the selected reference, used as a button title. */
  selectedBlockName?: string;
};

/**
 * Resolve UI metadata for a ribbon button from the command registry.
 *
 * The registry is the source of truth for `label` (translated via the
 * descriptor's `labelKeyRu`) and — by default — for `glyph` / `meta`
 * when those values are present in `uiMetadata` / `aliases.en`.
 *
 * Many ribbon buttons intentionally diverge from the registry for UX
 * reasons (e.g. 2-letter ribbon glyphs like "MV" vs the registry's
 * single-letter AutoCAD alias "M"). For those cases, pass
 * `{ keepGlyph: true }` and/or `{ keepMeta: true }` to keep the
 * fallback glyph / meta while still picking up the registry label.
 * The `ribbonMetaUx` shorthand below pre-applies both flags.
 *
 * The `fallback` parameter is used in full when the descriptor does
 * not exist in the registry (UI-only buttons or future registry
 * pruning) and per-field when the registry stores `null`.
 */
function ribbonMetaFromRegistry(
  commandId: string,
  fallback: { label: string; glyph: string; meta: string },
  options?: { keepGlyph?: boolean; keepMeta?: boolean },
): { label: string; glyph: string; meta: string } {
  const descriptor = getDescriptorById(
    commandId.toUpperCase(),
    ALL_COMMAND_DESCRIPTORS,
  );
  if (!descriptor) return fallback;
  return {
    label: translate(descriptor.uiMetadata.labelKeyRu as MessageKey),
    glyph: options?.keepGlyph
      ? fallback.glyph
      : (descriptor.uiMetadata.glyph ?? fallback.glyph),
    meta: options?.keepMeta
      ? fallback.meta
      : (descriptor.aliases.en[0] ?? fallback.meta),
  };
}

/**
 * Shorthand for buttons that take their `label` from the registry but
 * keep the ribbon's UX-tuned `glyph` and `meta` verbatim.
 *
 * `labelKey` is the i18n key resolved via the same `t()` shared with
 * the rest of the component; passing it as the message key keeps the
 * component re-rendering on language switch.
 */
function ribbonMetaUx(
  commandId: string,
  labelKey: MessageKey,
  glyph: string,
  meta: string,
): { label: string; glyph: string; meta: string } {
  return ribbonMetaFromRegistry(
    commandId,
    { label: translate(labelKey), glyph, meta },
    { keepGlyph: true, keepMeta: true },
  );
}

/**
 * Top-level builder for ribbon sections.
 *
 * @param cb              callbacks (translation, dispatchers, panel
 *                        openers).
 * @param isInBlockEdit   when true, returns the "block-editor" tab
 *                        only — normal tabs are hidden. Driven by
 *                        `state.drawing.activeSpaceId === BLOCK_EDIT_SPACE_ID`
 *                        from the App selector.
 * @param selectionContext optional "Контекст блока" payload — when
 *                        provided AND `isSingleBlockReference === true`,
 *                        the "Контекст блока" section is appended to
 *                        every regular tab so the user can enter
 *                        BEDIT from any active tab. Mirrors AutoCAD's
 *                        "Block Reference" contextual tab.
 */
export function buildRibbonSections(
  cb: RibbonCallbacks,
  isInBlockEdit: boolean = false,
  selectionContext?: RibbonSelectionContext,
): Record<RibbonTabId, RibbonSection[]> {
  if (isInBlockEdit) {
    return buildBlockEditorRibbonSections(cb);
  }
  const sections = buildNormalRibbonSections(cb);
  // Phase J.2 — append a "Контекст блока" section to every visible tab
  // when a single BlockReference is selected. Mirrors AutoCAD's
  // contextual tab behaviour. Multi-block selection deliberately does
  // not surface the section (the BEDIT command is ambiguous).
  if (selectionContext?.isSingleBlockReference) {
    const contextSection = buildBlockReferenceContextSection(
      cb,
      selectionContext.selectedBlockName,
    );
    for (const tabId of ribbonTabIds) {
      sections[tabId] = [...sections[tabId], contextSection];
    }
  }
  return sections;
}

function buildNormalRibbonSections(
  cb: RibbonCallbacks,
): Record<RibbonTabId, RibbonSection[]> {
  const {
    t,
    beginDrafting,
    openPanel,
    activateSpace,
    dispatch,
    onImportDxf,
    resetShellState,
    onOpenPrint,
    onOpenPageSetup,
  } = cb;
  return {
    // ── "Файл" / "File" tab ──
    // AutoCAD-like left-most entry point for application/output
    // commands. For now it carries a single "Вывод" / "Output" section
    // with the PRINT/PLOT button; clicking it opens the same page-setup
    // dialog reached via the PRINT / PLOT / ПЕЧАТЬ command line.
    file: [
      {
        title: t("ribbon.section.output"),
        actions: [
          {
            // UI-only: opens the PDF-6.9a Page Setup Manager. The
            // PAGESETUP / ПАРАМЛИСТ command-registry descriptor (when
            // it lands) will route through the same callback; for now
            // the ribbon button + the command-line handler in App.tsx
            // both call `pageSetupDialog.open()` via this callback.
            id: "pageSetup",
            label: t("ribbon.button.page_setup"),
            meta: "PAGESETUP",
            glyph: "PS",
            onClick: onOpenPageSetup,
          },
          {
            // UI-only: opens the print/plot dialog. The PRINT/PLOT
            // command-registry descriptor exists, but the dialog is
            // owned by the App-level `usePrintDialog` hook, so the
            // ribbon routes through the `onOpenPrint` callback rather
            // than a draft flow.
            id: "print",
            label: t("ribbon.button.print"),
            meta: "PLOT",
            glyph: "PT",
            onClick: onOpenPrint,
          },
        ],
      },
    ],
    draw: [
      {
        title: t("ribbon.section.drafting"),
        actions: [
          // LINE / CIRCLE / ARC: registry-only — all of label, glyph and meta
          // are read from the descriptor; the fallback values match registry
          // exactly so visual output is identical to the pre-migration code.
          {
            id: "line",
            ...ribbonMetaFromRegistry("LINE", {
              label: t("command.line.label"),
              glyph: "L",
              meta: "LINE",
            }),
            onClick: () => beginDrafting("LINE"),
          },
          {
            id: "circle",
            ...ribbonMetaFromRegistry("CIRCLE", {
              label: t("command.circle.label"),
              glyph: "C",
              meta: "CIRCLE",
            }),
            onClick: () => beginDrafting("CIRCLE"),
          },
          {
            id: "arc",
            ...ribbonMetaFromRegistry("ARC", {
              label: t("command.arc.label"),
              glyph: "A",
              meta: "ARC",
            }),
            onClick: () => beginDrafting("ARC"),
          },
        ],
      },
      {
        title: t("ribbon.section.blocks_arrays"),
        actions: [
          {
            id: "polyline",
            ...ribbonMetaUx("PLINE", "command.polyline.label", "P", "PLINE"),
            onClick: () => beginDrafting("PLINE"),
          },
          {
            id: "rectangle",
            ...ribbonMetaUx(
              "RECTANG",
              "command.rectangle.label",
              "R",
              "RECTANG",
            ),
            onClick: () => beginDrafting("RECTANG"),
          },
          {
            id: "block",
            ...ribbonMetaUx("BLOCK", "command.block.label", "B", "BLOCK"),
            onClick: () => beginDrafting("BLOCK", { definitionFlow: "true" }),
          },
          {
            id: "insert",
            ...ribbonMetaUx("INSERT", "command.insert.label", "I", "INSERT"),
            onClick: () => beginDrafting("INSERT"),
          },
          // ── BEDIT / BSAVE / BCLOSE (Phase 2.3) ──
          // BEDIT opens the Block Editor for an existing or new block.
          // BSAVE flushes the in-memory block-edit state to the
          // persistent BlockDefinition without closing the editor.
          // BCLOSE asks Save/Discard/Cancel and exits the editor.
          {
            id: "bedit",
            ...ribbonMetaUx("BEDIT", "command.bedit.label", "BE", "BEDIT"),
            onClick: () => beginDrafting("BEDIT"),
          },
          {
            id: "bsave",
            ...ribbonMetaUx("BSAVE", "command.bsave.label", "BS", "BSAVE"),
            onClick: () => beginDrafting("BSAVE"),
          },
          {
            id: "bclose",
            ...ribbonMetaUx("BCLOSE", "command.bclose.label", "BC", "BCLOSE"),
            onClick: () => beginDrafting("BCLOSE"),
          },
          {
            // Registry's ARRAYRECT label key reuses "command.arrayedit.label";
            // keep "command.array.label" hardcoded so the toolbar shows
            // "Массив" / "Array" instead of "Редактирование массива".
            id: "array",
            label: t("command.array.label"),
            meta: "ARRAYRECT",
            glyph: "AR",
            onClick: () => beginDrafting("ARRAYRECT"),
          },
          {
            id: "arraypolar",
            ...ribbonMetaUx(
              "ARRAYPOLAR",
              "command.arraypolar.label",
              "AP",
              "ARRAYPOLAR",
            ),
            onClick: () => beginDrafting("ARRAYPOLAR"),
          },
          {
            id: "arraypath",
            ...ribbonMetaUx(
              "ARRAYPATH",
              "command.arraypath.label",
              "APT",
              "ARRAYPATH",
            ),
            onClick: () => beginDrafting("ARRAYPATH"),
          },
          {
            id: "arrayedit",
            ...ribbonMetaUx(
              "ARRAYEDIT",
              "command.arrayedit.label",
              "AE",
              "ARRAYEDIT",
            ),
            onClick: () => beginDrafting("ARRAYEDIT"),
          },
        ],
      },
    ],
    annotate: [
      {
        title: t("ribbon.section.annotation"),
        actions: [
          {
            id: "text",
            ...ribbonMetaUx("TEXT", "command.text.label", "T", "TEXT"),
            onClick: () => beginDrafting("TEXT"),
          },
          {
            id: "mtext",
            ...ribbonMetaUx("MTEXT", "command.mtext.label", "MT", "MTEXT"),
            onClick: () => beginDrafting("MTEXT"),
          },
          {
            // Ribbon "DIM" meta is the dimension entry-point alias;
            // registry's canonical "DIMENSION" is more verbose.
            id: "dimension",
            ...ribbonMetaUx(
              "DIMENSION",
              "command.dimension.label",
              "DL",
              "DIM",
            ),
            onClick: () => beginDrafting("DIMENSION"),
          },
        ],
      },
      {
        title: t("ribbon.section.dimensions"),
        actions: [
          {
            id: "dimaligned",
            ...ribbonMetaUx(
              "DIMALIGNED",
              "command.dimaligned.label",
              "DA",
              "DAL",
            ),
            onClick: () => beginDrafting("DIMALIGNED"),
          },
          {
            id: "dimangular",
            ...ribbonMetaUx(
              "DIMANGULAR",
              "command.dimangular.label",
              "DN",
              "DAN",
            ),
            onClick: () => beginDrafting("DIMANGULAR"),
          },
          {
            id: "dim3point",
            ...ribbonMetaUx(
              "DIM3POINT",
              "command.dim3point.label",
              "D3",
              "DIM3POINT",
            ),
            onClick: () => beginDrafting("DIM3POINT"),
          },
          {
            id: "dimradius",
            ...ribbonMetaUx(
              "DIMRADIUS",
              "command.dimradius.label",
              "DR",
              "DRA",
            ),
            onClick: () => beginDrafting("DIMRADIUS"),
          },
          {
            id: "dimdiameter",
            ...ribbonMetaUx(
              "DIMDIAMETER",
              "command.dimdiameter.label",
              "DD",
              "DDI",
            ),
            onClick: () => beginDrafting("DIMDIAMETER"),
          },
          {
            id: "dimordinate",
            ...ribbonMetaUx(
              "DIMORDINATE",
              "command.dimordinate.label",
              "DO",
              "DOR",
            ),
            onClick: () => beginDrafting("DIMORDINATE"),
          },
        ],
      },
      {
        title: t("ribbon.section.callouts"),
        actions: [
          {
            id: "mleader",
            ...ribbonMetaUx(
              "MLEADER",
              "command.mleader.label",
              "ML",
              "MLEADER",
            ),
            onClick: () => beginDrafting("MLEADER"),
          },
          {
            // MLEADEREDIT descriptor has a single label; ribbon exposes two
            // distinct UX actions (add-leader / remove-leader) with their
            // own translated labels — keep both hardcoded.
            id: "mleader-add-leader",
            label: t("ribbon.action.mleader_add_leader"),
            meta: "MLEADEREDIT",
            glyph: "+L",
            onClick: () => beginDrafting("MLEADEREDIT", { mode: "add" }),
          },
          {
            id: "mleader-remove-leader",
            label: t("ribbon.action.mleader_remove_leader"),
            meta: "MLEADEREDIT",
            glyph: "-L",
            onClick: () => beginDrafting("MLEADEREDIT", { mode: "remove" }),
          },
          {
            id: "revcloud",
            ...ribbonMetaFromRegistry("REVCLOUD", {
              label: t("command.revcloud.label"),
              glyph: "RC",
              meta: "REVCLOUD",
            }),
            onClick: () => beginDrafting("REVCLOUD"),
          },
          {
            id: "hatch",
            ...ribbonMetaFromRegistry("HATCH", {
              label: t("command.hatch.label"),
              glyph: "H",
              meta: "HATCH",
            }),
            onClick: () => beginDrafting("HATCH"),
          },
          {
            // UI-only: opens panel; no command-registry counterpart.
            id: "history",
            label: t("ribbon.action.history"),
            meta: "PANEL",
            glyph: "H",
            onClick: () => openPanel("history"),
          },
          {
            // UI-only: opens AI panel; no command-registry counterpart.
            id: "ai",
            label: t("ribbon.action.ai"),
            meta: "GUARD",
            glyph: "AI",
            onClick: () => openPanel("ai"),
          },
        ],
      },
    ],
    modify: [
      {
        title: t("ribbon.section.modify"),
        actions: [
          // Modify family: ribbon uses 2-letter UX glyphs (MV/CP/MI/...);
          // registry has single-letter AutoCAD aliases as glyphs. Meta uses
          // the short alias instead of the canonical long form. ribbonMetaUx
          // keeps both verbatim and only adopts the registry label.
          {
            id: "move",
            ...ribbonMetaUx("MOVE", "command.move.label", "MV", "M"),
            onClick: () => beginDrafting("MOVE"),
          },
          {
            id: "copy",
            ...ribbonMetaUx("COPY", "command.copy.label", "CP", "CO"),
            onClick: () => beginDrafting("COPY"),
          },
          {
            id: "mirror",
            ...ribbonMetaUx("MIRROR", "command.mirror.label", "MI", "MI"),
            onClick: () => beginDrafting("MIRROR"),
          },
          {
            id: "offset",
            ...ribbonMetaUx("OFFSET", "command.offset.label", "OF", "O"),
            onClick: () => beginDrafting("OFFSET"),
          },
          {
            id: "rotate",
            ...ribbonMetaUx("ROTATE", "command.rotate.label", "RO", "RO"),
            onClick: () => beginDrafting("ROTATE"),
          },
          {
            id: "scale",
            ...ribbonMetaUx("SCALE", "command.scale.label", "SC", "SC"),
            onClick: () => beginDrafting("SCALE"),
          },
          {
            id: "stretch",
            ...ribbonMetaUx("STRETCH", "command.stretch.label", "ST", "S"),
            onClick: () => beginDrafting("STRETCH"),
          },
          {
            id: "trim",
            ...ribbonMetaUx("TRIM", "command.trim.label", "TR", "TR"),
            onClick: () => beginDrafting("TRIM"),
          },
          {
            id: "extend",
            ...ribbonMetaUx("EXTEND", "command.extend.label", "EX", "EX"),
            onClick: () => beginDrafting("EXTEND"),
          },
          {
            id: "fillet",
            ...ribbonMetaUx("FILLET", "command.fillet.label", "FL", "F"),
            onClick: () => beginDrafting("FILLET"),
          },
          {
            id: "chamfer",
            ...ribbonMetaUx("CHAMFER", "command.chamfer.label", "CH", "CHA"),
            onClick: () => beginDrafting("CHAMFER"),
          },
          {
            id: "break",
            ...ribbonMetaUx("BREAK", "command.break.label", "BK", "BR"),
            onClick: () => beginDrafting("BREAK"),
          },
          {
            id: "join",
            ...ribbonMetaUx("JOIN", "command.join.label", "JN", "J"),
            onClick: () => beginDrafting("JOIN"),
          },
          {
            id: "explode",
            ...ribbonMetaUx("EXPLODE", "command.explode.label", "XP", "X"),
            onClick: () => beginDrafting("EXPLODE"),
          },
          {
            id: "lengthen",
            ...ribbonMetaUx("LENGTHEN", "command.lengthen.label", "LN", "LEN"),
            onClick: () => beginDrafting("LENGTHEN"),
          },
        ],
      },
      {
        title: t("ribbon.section.properties"),
        actions: [
          {
            // CECOLOR — current-entity-colour swatch (AutoCAD's "Object Color"
            // ribbon control). A custom React control, not a plain button: the
            // ribbon renderer (App.tsx) renders <CeColorSwatch/> for this id.
            // onClick is a no-op (the swatch owns its own click → picker flow).
            id: "cecolor-swatch",
            label: t("cecolor.swatch.tooltip"),
            meta: "COLOR",
            glyph: "CO",
            onClick: () => {},
          },
          {
            // Registry has PROPERTIES descriptor but its label is the verbose
            // CLI form ("СВОЙСТВА (Свойства)"); ribbon uses a concise label.
            id: "properties",
            label: t("ribbon.action.properties"),
            meta: "PROPERTIES",
            glyph: "PR",
            onClick: () =>
              dispatch({
                type: "commandline/submit",
                source: "toolbar",
                text: "PROPERTIES",
              }),
          },
          {
            id: "chprop",
            label: t("ribbon.action.chprop"),
            meta: "BATCH",
            glyph: "CP",
            onClick: () => openPanel("properties"),
          },
          {
            id: "matchprop",
            label: t("ribbon.action.matchprop"),
            meta: "STYLE",
            glyph: "MP",
            onClick: () => openPanel("properties"),
          },
        ],
      },
      {
        title: t("ribbon.section.selection"),
        actions: [
          {
            // UI-only: clears selection; no command-registry descriptor.
            id: "clear-selection",
            label: t("ribbon.action.clear"),
            meta: "ESC",
            glyph: "X",
            onClick: () =>
              dispatch({
                type: "shell/clear-selection",
                source: "toolbar",
              }),
          },
          {
            // UI-only: opens the overview panel.
            id: "overview",
            label: t("ribbon.action.overview"),
            meta: "SCOPE",
            glyph: "O",
            onClick: () => openPanel("overview"),
          },
          {
            // UI-only: re-uses the history panel for an audit log view.
            id: "audit",
            label: t("ribbon.action.audit"),
            meta: "LOG",
            glyph: "AU",
            onClick: () => openPanel("history"),
          },
        ],
      },
    ],
    layout: [
      {
        title: t("ribbon.section.space_routing"),
        actions: [
          {
            // UI-only space-routing buttons — registry's MSPACE/PSPACE/LAYOUT
            // descriptors describe CLI commands with verbose CLI labels;
            // the ribbon uses concise toolbar labels via ribbon.action.*.
            id: "model",
            label: t("ribbon.action.model"),
            meta: "MODEL",
            glyph: "M",
            onClick: () => activateSpace("model"),
          },
          {
            id: "layout-1",
            label: t("ribbon.action.layout_1"),
            meta: "LAYOUT",
            glyph: "L1",
            onClick: () => activateSpace("layout-1", "paper"),
          },
          {
            id: "layout-2",
            label: t("ribbon.action.layout_2"),
            meta: "LAYOUT",
            glyph: "L2",
            onClick: () => activateSpace("layout-2", "paper"),
          },
        ],
      },
      {
        title: t("ribbon.section.viewport_context"),
        actions: [
          {
            id: "paper",
            label: t("ribbon.action.paper"),
            meta: "PSPACE",
            glyph: "PS",
            onClick: () => activateSpace("layout-1", "paper"),
          },
          {
            id: "viewport",
            label: t("ribbon.action.viewport"),
            meta: "MSPACE",
            glyph: "MS",
            onClick: () => activateSpace("layout-1", "viewport"),
          },
          {
            // Registry has MVIEW with verbose CLI label; ribbon uses concise.
            id: "viewports",
            label: t("ribbon.action.viewports"),
            meta: "MVIEW",
            glyph: "VP",
            onClick: () => beginDrafting("MVIEW"),
          },
        ],
      },
    ],
    propertyflows: [
      {
        title: t("ribbon.section.inspectors"),
        actions: [
          {
            // UI-only panel-opener buttons.
            id: "panel-overview",
            label: t("ribbon.action.panel_overview"),
            meta: "PANEL",
            glyph: "OV",
            onClick: () => openPanel("overview"),
          },
          {
            id: "panel-properties",
            label: t("ribbon.action.panel_properties"),
            meta: "PANEL",
            glyph: "PP",
            onClick: () => openPanel("properties"),
          },
          {
            id: "panel-history",
            label: t("ribbon.action.panel_history"),
            meta: "PANEL",
            glyph: "HI",
            onClick: () => openPanel("history"),
          },
        ],
      },
      {
        title: t("ribbon.section.data_flow"),
        actions: [
          {
            // UI-only: file import handler.
            id: "dxf",
            label: t("ribbon.action.import_dxf"),
            meta: "FILE",
            glyph: "DX",
            onClick: onImportDxf,
          },
          {
            id: "reset",
            label: t("ribbon.action.reset"),
            meta: "STATE",
            glyph: "RS",
            onClick: resetShellState,
          },
          {
            id: "session",
            label: t("ribbon.action.sessions"),
            meta: "RBAC",
            glyph: "RB",
            onClick: () =>
              window.scrollTo({
                top: document.body.scrollHeight,
                behavior: "smooth",
              }),
          },
        ],
      },
    ],
    // The "block-editor" tab is only populated by
    // `buildBlockEditorRibbonSections`. Keep it empty here to satisfy
    // the Record<RibbonTabId, RibbonSection[]> type — App.tsx switches
    // between `ribbonTabIds` and `blockEditorRibbonTabIds` so this slot
    // is never rendered while in the normal mode.
    "block-editor": [],
  };
}

/**
 * Phase J.2 — "Контекст блока" / "Block Reference" contextual section.
 *
 * Appended to every regular tab when a single BlockReference is the
 * current selection. Contains the prominent "Войти в редактор блоков"
 * (BE) button and a few quick-jump aliases that mirror AutoCAD's
 * contextual ribbon for block insertions (Edit Block In-Place, Test
 * Block, Edit Attributes).
 */
function buildBlockReferenceContextSection(
  cb: RibbonCallbacks,
  selectedBlockName: string | undefined,
): RibbonSection {
  const { t, beginDrafting } = cb;
  return {
    title: t("ribbon.context.block.label"),
    actions: [
      {
        id: "context-bedit",
        label: t("ribbon.context.block.enter_editor"),
        meta: "BEDIT",
        glyph: "BE",
        // Surface the selected block name in the tooltip so the user
        // sees what they are about to edit. Falls back to the i18n
        // label when the selection lookup hasn't seeded the name.
        onClick: () =>
          beginDrafting(
            "BEDIT",
            selectedBlockName ? { blockName: selectedBlockName } : undefined,
          ),
      },
      {
        id: "context-testblock",
        label: t("ribbon.context.block.test_block"),
        meta: "TESTBLOCK",
        glyph: "TB",
        // TESTBLOCK is not a draft-flow command — route through
        // commandline/submit so the existing TESTBLOCK handler (in
        // shellUiReducer / draftStateMachine) takes the active block
        // and opens the sandbox preview space.
        onClick: () =>
          cb.dispatch({
            type: "commandline/submit",
            source: "toolbar",
            text: selectedBlockName
              ? `TESTBLOCK ${selectedBlockName}`
              : "TESTBLOCK",
          }),
      },
      {
        id: "context-eattedit",
        label: t("ribbon.context.block.edit_attributes"),
        meta: "EATTEDIT",
        glyph: "EA",
        onClick: () => beginDrafting("EATTEDIT"),
      },
    ],
  };
}

/**
 * Phase J.3 — Build ribbon sections for the Block Editor mode.
 *
 * Mirrors AutoCAD's "Block Editor" ribbon tab structure exactly:
 *  - "Параметры" / "Parameters"  — opens authoring palette (Phase K)
 *  - "Действия" / "Actions"      — opens authoring palette (Phase K)
 *  - "Видимость" / "Visibility"  — opens visibility states manager
 *                                   (Phase K)
 *  - "Управление"                — Save / SaveAs (BSAVE) + Test
 *                                   block (TESTBLOCK)
 *  - "Черчение"  / "Drawing"     — LINE / CIRCLE / ARC / RECTANG /
 *                                   PLINE
 *  - "Редактирование" / "Editing" — MOVE / COPY / ROTATE / MIRROR /
 *                                   SCALE / TRIM / ERASE
 *  - "Закрыть" / "Close"         — large red "Закрыть редактор блока"
 *                                   button
 *
 * The non-block-editor tabs (Layout / Annotate / Modify / Properties)
 * are intentionally hidden — block-edit mode does not have a layout /
 * paper-space concept and the Authoring sections supersede the normal
 * Modify tab.
 */
function buildBlockEditorRibbonSections(
  cb: RibbonCallbacks,
): Record<RibbonTabId, RibbonSection[]> {
  const { t, beginDrafting, dispatch, onOpenBlockAuthoringPalette } = cb;
  // ── Phase K placeholder — palette/visibility manager not yet wired.
  // Until Phase K lands, clicking these buttons surfaces a toast via
  // the App-level handler.
  const onPalette = (
    pane: "parameters" | "actions" | "visibility" | "parameter-sets",
  ) => onOpenBlockAuthoringPalette?.(pane);

  const sections: RibbonSection[] = [
    {
      title: t("ribbon.block_editor.parameters"),
      actions: [
        {
          id: "bauthor-parameters",
          label: t("ribbon.block_editor.parameters"),
          meta: "BPARAMETER",
          glyph: "PA",
          onClick: () => onPalette("parameters"),
        },
        {
          id: "bauthor-parameter-sets",
          label: t("ribbon.block_editor.parameter-sets"),
          meta: "BPARAMETERSET",
          glyph: "PS",
          onClick: () => onPalette("parameter-sets"),
        },
      ],
    },
    {
      title: t("ribbon.block_editor.actions"),
      actions: [
        {
          id: "bauthor-actions",
          label: t("ribbon.block_editor.actions"),
          meta: "BACTION",
          glyph: "AC",
          onClick: () => onPalette("actions"),
        },
      ],
    },
    {
      title: t("ribbon.block_editor.visibility"),
      actions: [
        {
          id: "bauthor-visibility",
          label: t("ribbon.block_editor.visibility"),
          meta: "BVSTATE",
          glyph: "VI",
          onClick: () => onPalette("visibility"),
        },
      ],
    },
    {
      title: t("ribbon.block_editor.management"),
      actions: [
        {
          id: "bedit-bsave",
          ...ribbonMetaUx("BSAVE", "command.bsave.label", "BS", "BSAVE"),
          onClick: () => beginDrafting("BSAVE"),
        },
        {
          id: "bedit-testblock",
          label: t("ribbon.block_editor.test_block"),
          meta: "TESTBLOCK",
          glyph: "TB",
          // TESTBLOCK has no draft-flow registration — it's handled via
          // command-line submission which opens a sandbox preview
          // space using the currently-edited block name.
          onClick: () =>
            dispatch({
              type: "commandline/submit",
              source: "toolbar",
              text: "TESTBLOCK",
            }),
        },
      ],
    },
    {
      title: t("ribbon.block_editor.drawing"),
      actions: [
        {
          id: "be-line",
          ...ribbonMetaFromRegistry("LINE", {
            label: t("command.line.label"),
            glyph: "L",
            meta: "LINE",
          }),
          onClick: () => beginDrafting("LINE"),
        },
        {
          id: "be-circle",
          ...ribbonMetaFromRegistry("CIRCLE", {
            label: t("command.circle.label"),
            glyph: "C",
            meta: "CIRCLE",
          }),
          onClick: () => beginDrafting("CIRCLE"),
        },
        {
          id: "be-arc",
          ...ribbonMetaFromRegistry("ARC", {
            label: t("command.arc.label"),
            glyph: "A",
            meta: "ARC",
          }),
          onClick: () => beginDrafting("ARC"),
        },
        {
          id: "be-rectang",
          ...ribbonMetaUx(
            "RECTANG",
            "command.rectangle.label",
            "R",
            "RECTANG",
          ),
          onClick: () => beginDrafting("RECTANG"),
        },
        {
          id: "be-pline",
          ...ribbonMetaUx("PLINE", "command.polyline.label", "P", "PLINE"),
          onClick: () => beginDrafting("PLINE"),
        },
      ],
    },
    {
      title: t("ribbon.block_editor.editing"),
      actions: [
        {
          id: "be-move",
          ...ribbonMetaUx("MOVE", "command.move.label", "MV", "M"),
          onClick: () => beginDrafting("MOVE"),
        },
        {
          id: "be-copy",
          ...ribbonMetaUx("COPY", "command.copy.label", "CP", "CO"),
          onClick: () => beginDrafting("COPY"),
        },
        {
          id: "be-rotate",
          ...ribbonMetaUx("ROTATE", "command.rotate.label", "RO", "RO"),
          onClick: () => beginDrafting("ROTATE"),
        },
        {
          id: "be-mirror",
          ...ribbonMetaUx("MIRROR", "command.mirror.label", "MI", "MI"),
          onClick: () => beginDrafting("MIRROR"),
        },
        {
          id: "be-scale",
          ...ribbonMetaUx("SCALE", "command.scale.label", "SC", "SC"),
          onClick: () => beginDrafting("SCALE"),
        },
        {
          id: "be-trim",
          ...ribbonMetaUx("TRIM", "command.trim.label", "TR", "TR"),
          onClick: () => beginDrafting("TRIM"),
        },
        {
          id: "be-erase",
          // ERASE has no draft session — dispatch shell/delete-entities
          // directly using the current selection. The button is disabled
          // visually when nothing is selected; for now just route the
          // command line.
          label: t("ribbon.block_editor.erase"),
          meta: "ERASE",
          glyph: "ER",
          onClick: () =>
            dispatch({
              type: "commandline/submit",
              source: "toolbar",
              text: "ERASE",
            }),
        },
      ],
    },
    {
      title: t("ribbon.block_editor.close_section"),
      actions: [
        {
          id: "bedit-bclose",
          ...ribbonMetaUx(
            "BCLOSE",
            "ribbon.block_editor.close",
            "BC",
            "BCLOSE",
          ),
          onClick: () => beginDrafting("BCLOSE"),
          variant: "danger",
        },
      ],
    },
  ];

  return {
    // Normal tabs are hidden in block-edit mode — the Record entries
    // are populated with empty arrays so the type stays satisfied
    // while the App-level renderer iterates `blockEditorRibbonTabIds`
    // (which contains only "block-editor").
    file: [],
    draw: [],
    annotate: [],
    modify: [],
    layout: [],
    propertyflows: [],
    "block-editor": sections,
  };
}
